import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toF = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "object") {
    v = v["$n"] ?? v["value"] ?? v["S"] ?? Object.values(v)[0] ?? 0;
  }
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
};
const q2 = (v) => Math.round(v * 100) / 100;

function parseDate(str) {
  if (!str) return null;
  if (/^\d{2}-\d{2}-\d{4}/.test(str)) {
    const [d, m, y] = str.split(/[-\s]/);
    return new Date(`${y}-${m}-${d}`);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return new Date(`${y}-${m}-${d}`);
  }
  return new Date(str);
}

const COLORS = {
  green: "#4ade80", red: "#f87171", yellow: "#E6FF3E",
  blue: "#60a5fa", orange: "#fb923c", purple: "#c084fc",
  teal: "#2dd4bf", gray: "#64748b",
};

const SEUIL_CR     = 60;
const SEUIL_EP_VAR = 2;

const normPn = (s) =>
  String(s || "")
    .trim()
    .replace(/\s*-\s*(\d+)$/, "-$1") // " - 845" → "-845"
    .toLowerCase();

// ── Sous-composants ───────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.kpiCard, borderTopColor: color }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

function DefautBadge({ actif, label }) {
  return actif ? (
    <span style={{ ...S.badge, background: "rgba(248,113,113,0.15)", color: COLORS.red, borderColor: "rgba(248,113,113,0.3)" }}>
      ⚠️ {label}
    </span>
  ) : (
    <span style={{ ...S.badge, background: "rgba(74,222,128,0.08)", color: "#64748b", borderColor: "rgba(74,222,128,0.15)" }}>
      ✓ {label}
    </span>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ color: "#E6FF3E", fontWeight: 700, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontSize: 12, marginBottom: 2 }}>
          {p.name} : <strong>{typeof p.value === "number" ? q2(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardTracabiliteStack() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState("");
  const [stacks,            setStacks]            = useState([]);       // qualite/stack/list
  const [empList,           setEmpList]           = useState([]);       // empilement/list/impregnation
  const [bancTests,         setBancTests]         = useState([]);       // bancdetest/list
  const [selectedEmpDetail, setSelectedEmpDetail] = useState(null);     // empilement/<id>
  const [loadingDetail,     setLoadingDetail]     = useState(false);
  const [search,            setSearch]            = useState("");
  const [selectedID,        setSelectedID]        = useState(null);
  const [activeTab,         setActiveTab]         = useState("fiche");

  // ── Chargement initial ───────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          apiFetch("/api/qualite/stack/list"),
          apiFetch("/api/empilement/list/impregnation"),
          apiFetch("/api/bancdetest/list"),
        ]);

        const safe = async (r) => {
          if (r.status === "rejected") return [];
          try {
            const d = await r.value.json();
            return Array.isArray(d) ? d : d.items || [];
          } catch { return []; }
        };

        const [s, e, b] = await Promise.all(results.map(safe));

        setStacks(s);
        setEmpList(e);
        setBancTests(b);

        // Sélectionne le premier stack par défaut
        if (e.length > 0) {
          const firstId = e[0].StackId || e[0].IDStack;
          setSelectedID(firstId);
          loadEmpDetail(firstId);
        } else if (s.length > 0) {
          setSelectedID(s[0].IDStack);
        }
      } catch {
        setError("Erreur chargement données traçabilité.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Chargement détail empilement ─────────────────────────────────────────────
  const loadEmpDetail = async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    setSelectedEmpDetail(null);
    try {
      const res = await apiFetch(`/api/empilement/${encodeURIComponent(id)}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedEmpDetail(data);
        // ── LOG ──────────────────────────────────────────────────────
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("🪵 [loadEmpDetail] Stack:", id);
      console.log("📋 rows reçus:", data.rows);
      console.log("🔑 plyPns des plis:", data.rows?.map(r => r.plyPn));
      // ─────────────────────────────────────────────────────────────
      }
    } catch (e) {
      console.error("Erreur détail empilement:", e);
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Sélection d'un stack ─────────────────────────────────────────────────────
  const handleSelectStack = (id) => {
    setSelectedID(id);
    setActiveTab("fiche");
    loadEmpDetail(id);
  };

  // ── Plis depuis le détail ────────────────────────────────────────────────────
  const plisStack = useMemo(() =>
    (selectedEmpDetail?.rows || []).sort((a, b) => a.position - b.position),
  [selectedEmpDetail]);

  // ── Map plyPn → test BancDeTest ──────────────────────────────────────────────
  const bancTestMap = useMemo(() => {
    const map = {};
    bancTests.forEach(b => { map[normPn(b.Lot_woodoo)] = b; });
    return map;
  }, [bancTests]);

  // ── Tests BancDeTest du stack sélectionné ───────────────────────────────────
  const plyPnsStack = useMemo(() =>
    new Set(plisStack.map(p => normPn(p.plyPn))),
  [plisStack]);

  const testsStack = useMemo(() =>
    bancTests.filter(b => plyPnsStack.has(normPn(b.Lot_woodoo))),
  [bancTests, plyPnsStack]);

  // ── Plis enrichis avec BancDeTest ───────────────────────────────────────────
  const plisEnrichis = useMemo(() => {
  // ── LOG diagnostic ─────────────────────────────────────────────
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("🗺️ bancTestMap keys (5 premiers):", Object.keys(bancTestMap).slice(0, 5));
  console.log("🪵 plisStack plyPns (5 premiers):", plisStack.slice(0, 5).map(p => p.plyPn));
  console.log("🔍 Test croisement pli[0]:");
  if (plisStack[0]) {
    const key = normPn(plisStack[0].plyPn);
    console.log("   plyPn brut     :", plisStack[0].plyPn);
    console.log("   plyPn normalisé:", key);
    console.log("   bt trouvé      :", bancTestMap[key] ? "✅ OUI" : "❌ NON");
    console.log("   bancTestMap[key]:", bancTestMap[key]);
  }
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  // ──────────────────────────────────────────────────────────────

  return plisStack.map(p => {
    const key = normPn(p.plyPn);
    const bt  = bancTestMap[key];
    return {
      ...p,
      humidite: toF(bt?.Humidite_Moyenne),
      densite:  toF(bt?.Densite),
      epaisseur:toF(bt?.Epaisseur),
      masse:    toF(bt?.Masse),
      largeur:  toF(bt?.Largeur1 || bt?.Largeur2),
      longueur: toF(bt?.Longueur1 || bt?.Longueur2),
    };
  });
}, [plisStack, bancTestMap]);

  // ── Fiche stack fusionnée ───────────────────────────────────────────────────
  const selectedStack = useMemo(() => {
    const qualite = stacks.find(s => s.IDStack === selectedID);
    const empBase  = empList.find(e => (e.StackId || e.IDStack) === selectedID);
    if (!qualite && !empBase && !selectedEmpDetail) return null;
    return {
      IDStack:                  selectedID,
      NbPlis:                   selectedEmpDetail?.totalPlis ?? toF(empBase?.TotalPlis),
      Date:                     qualite?.Date || qualite?.Timestamp || "—",
      Operateur:                qualite?.Operateur || "—",
      Conformite:               qualite?.Conformite || null,
      Taux_Compression:         toF(qualite?.Taux_Compression)        || null,
      Ecart_Max_Ep:             toF(qualite?.Ecart_Max_Ep)            || null,
      Densite_Stack:            toF(qualite?.Densite_Stack)           || null,
      Epaisseur_Moyenne:        toF(qualite?.Epaisseur_Moyenne)       || null,
      Longueur:                 toF(qualite?.Longueur)                || null,
      Largeur:                  toF(qualite?.Largeur)                 || null,
      Masse:                    toF(qualite?.Masse)                   || null,
      Fraction_Massique_Bois:   toF(qualite?.Fraction_Massique_Bois)  || null,
      Fraction_Massique_Resine: toF(qualite?.Fraction_Massique_Resine)|| null,
      Delamination:             qualite?.Delamination  ?? false,
      DefautSurface:            qualite?.DefautSurface ?? false,
      Planeite:                 qualite?.Planeite      ?? false,
      AutresDefauts:            qualite?.AutresDefauts ?? false,
      DelaminationComment:      qualite?.DelaminationComment   || "",
      DefautSurfaceComment:     qualite?.DefautSurfaceComment  || "",
      PlaneiteComment:          qualite?.PlaneiteComment       || "",
      AutresDefautsComment:     qualite?.AutresDefautsComment  || "",
    };
  }, [selectedID, stacks, empList, selectedEmpDetail]);

  // ── Données graphique plis ───────────────────────────────────────────────────
  const epChart = useMemo(() =>
    plisEnrichis
      .map(p => ({
        pli:      `P${p.position}`,
        ep:       p.epaisseur,
        humidite: p.humidite,
        densite:  p.densite,
      }))
      .filter(p => p.ep > 0 || p.humidite > 0),
  [plisEnrichis]);

  // ── Données CR ───────────────────────────────────────────────────────────────
  const crData = useMemo(() =>
    stacks
      .map(s => ({
        stack:    s.IDStack,
        cr:       q2(toF(s.Taux_Compression)),
        color:    toF(s.Taux_Compression) > SEUIL_CR ? COLORS.red : COLORS.green,
        conforme: s.Conformite,
        date:     s.Date || s.Timestamp || "",
      }))
      .filter(r => r.cr > 0)
      .sort((a, b) => parseDate(b.date) - parseDate(a.date))
      .slice(0, 30),
  [stacks]);

  // ── Stacks avec défauts ──────────────────────────────────────────────────────
  const defautsData = useMemo(() =>
    stacks.filter(s =>
      s.Delamination  === true || s.Delamination  === "true" ||
      s.DefautSurface === true || s.DefautSurface === "true" ||
      s.Planeite      === true || s.Planeite      === "true" ||
      s.AutresDefauts === true || s.AutresDefauts === "true" ||
      toF(s.Ecart_Max_Ep) > SEUIL_EP_VAR
    ).sort((a, b) => parseDate(b.Date || b.Timestamp) - parseDate(a.Date || a.Timestamp)),
  [stacks]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:       empList.length,
    avecDefauts: defautsData.length,
    crAlerte:    crData.filter(r => r.cr > SEUIL_CR).length,
    nbPlis:      plisStack.length,
    nbTests:     testsStack.length,
    epMoy:       selectedStack?.Epaisseur_Moyenne
                   ? q2(selectedStack.Epaisseur_Moyenne) : null,
  }), [empList, defautsData, crData, plisStack, testsStack, selectedStack]);

  // ── Options dropdown ─────────────────────────────────────────────────────────
  const stackOptions = useMemo(() =>
    empList
      .filter(e => !search.trim() ||
        (e.StackId || e.IDStack || "").toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const idA = a.StackId || a.IDStack || "";
        const idB = b.StackId || b.IDStack || "";
        return idB.localeCompare(idA);
      })
      .slice(0, 200),
  [empList, search]);

  // ── Rendu ─────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#E6FF3E", fontSize: 16 }}>⏳ Chargement…</div>
    </div>
  );
  if (error) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#f87171" }}>{error}</div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div>
            <div style={S.title}>🏭 Traçabilité Stack</div>
            <div style={S.subtitle}>Fiche stack · Taux compression · Carte des défauts</div>
          </div>
          <button style={S.backBtn} onClick={() => navigate(-1)}>← Retour</button>
        </div>

        {/* ── KPIs ── */}
        <div style={S.kpiRow}>
          <KpiCard icon="📦" label="Stacks total"          value={stats.total}       sub="tous les empilements"          color={COLORS.blue}   />
          <KpiCard icon="⚠️" label="Stacks avec défauts"   value={stats.avecDefauts} sub="délamination/surface/planéité"  color={stats.avecDefauts > 0 ? COLORS.red : COLORS.green} />
          <KpiCard icon="📉" label={`Alertes CR > ${SEUIL_CR}%`} value={stats.crAlerte} sub="taux compression excessif"  color={stats.crAlerte > 0 ? COLORS.orange : COLORS.green} />
          <KpiCard icon="🪵" label="Plis du stack"          value={stats.nbPlis}      sub={selectedID || "—"}             color={COLORS.teal}   />
          <KpiCard icon="🔬" label="Tests BancDeTest"       value={stats.nbTests}     sub={selectedID || "—"}             color={COLORS.purple} />
          <KpiCard icon="📐" label="Ép. moy. stack"         value={stats.epMoy ? `${stats.epMoy} mm` : "—"} sub="stack sélectionné" color={COLORS.yellow} />
        </div>

        {/* ── Tableau tous les stacks ── */}
      {/* ── Grid de cards stacks ── */}
<div style={S.card}>
  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
    <div style={S.cardTitle}>📦 Tous les stacks ({stackOptions.length})</div>
    <input
      style={{ ...S.searchInput, width: 240, marginBottom: 0 }}
      placeholder="Rechercher un IDStack…"
      value={search}
      onChange={e => setSearch(e.target.value)}
    />
  </div>

  <div style={{
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 10,
    maxHeight: 420,
    overflowY: "auto",
    paddingRight: 4,
  }}>
    {stackOptions.map((e, i) => {
      const id      = e.StackId || e.IDStack;
      const qualite = stacks.find(s => s.IDStack === id);
      const conforme = qualite?.Conformite;
      const isSelected = selectedID === id;

      return (
        <div
          key={i}
          onClick={() => handleSelectStack(id)}
          style={{
            padding: "12px 14px",
            borderRadius: 10,
            border: `1px solid ${
              isSelected         ? "#E6FF3E"
              : conforme === "CONFORME" ? "rgba(74,222,128,0.25)"
              : conforme         ? "rgba(248,113,113,0.25)"
              : "#1e3a50"
            }`,
            background: isSelected
              ? "rgba(230,255,62,0.07)"
              : "rgba(255,255,255,0.02)",
            cursor: "pointer",
            transition: "all 0.15s ease",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* ID */}
          <div style={{
            fontFamily: "monospace",
            fontWeight: 800,
            fontSize: 12,
            color: isSelected ? "#E6FF3E" : "#cbd5e1",
            wordBreak: "break-all",
          }}>
            {id}
          </div>

          {/* Badge conformité */}
          {conforme && (
            <span style={{
              ...S.badge,
              fontSize: 10,
              padding: "2px 8px",
              background: conforme === "CONFORME" ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.15)",
              color:      conforme === "CONFORME" ? COLORS.green : COLORS.red,
              borderColor:conforme === "CONFORME" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)",
            }}>
              {conforme === "CONFORME" ? "✓ Conforme" : "✗ Non conforme"}
            </span>
          )}

        </div>
      );
    })}
  </div>
</div>

        {/* ── Tabs ── */}
        <div style={S.tabBar}>
          {[
            { key: "fiche",   label: "📋 Fiche Stack" },
            { key: "cr",      label: "📉 Vue CR" },
            { key: "defauts", label: `⚠️ Carte Défauts (${defautsData.length})` },
          ].map(t => (
            <button key={t.key}
              style={{ ...S.tab, ...(activeTab === t.key ? S.tabActive : {}) }}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══ TAB 1 — FICHE STACK ══ */}
        {activeTab === "fiche" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {!selectedID && (
              <div style={S.empty}>👆 Sélectionne un stack dans le tableau ci-dessus</div>
            )}

            {selectedID && loadingDetail && (
              <div style={{ ...S.card, textAlign: "center", color: "#E6FF3E" }}>
                ⏳ Chargement des plis de {selectedID}…
              </div>
            )}

            {selectedID && !loadingDetail && selectedStack && (
              <>
                {/* Infos générales */}
                <div style={S.card}>
                  <div style={S.cardTitle}>📋 Fiche — {selectedStack.IDStack}</div>
                  <div style={S.ficheGrid}>
                    {[
                      ["Date",             selectedStack.Date],
                      ["Opérateur",        selectedStack.Operateur],
                      ["Nb plis",          selectedStack.NbPlis || "—"],
                      ["Masse (g)",         selectedStack.Masse ? `${selectedStack.Masse} g` : "—"],
                      ["Longueur",         selectedStack.Longueur ? `${selectedStack.Longueur} mm` : "—"],
                      ["Largeur",          selectedStack.Largeur  ? `${selectedStack.Largeur} mm`  : "—"],
                      ["Ép. moyenne",      selectedStack.Epaisseur_Moyenne ? `${selectedStack.Epaisseur_Moyenne} mm` : "—"],
                      ["Densité stack",    selectedStack.Densite_Stack ? `${selectedStack.Densite_Stack} g/cm³` : "—"],
                      ["Taux CR",          selectedStack.Taux_Compression ? `${selectedStack.Taux_Compression} %` : "—"],
                      ["Écart max ép.",    selectedStack.Ecart_Max_Ep ? `${selectedStack.Ecart_Max_Ep} mm` : "—"],
                      ["Fraction bois",    selectedStack.Fraction_Massique_Bois ? `${selectedStack.Fraction_Massique_Bois} %` : "—"],
                      ["Fraction résine",  selectedStack.Fraction_Massique_Resine ? `${selectedStack.Fraction_Massique_Resine} %` : "—"],
                      ["Conformité",       selectedStack.Conformite || "—"],
                    ].map(([label, val]) => (
                      <div key={label} style={S.ficheItem}>
                        <span style={S.ficheLabel}>{label}</span>
                        <span style={{
                          ...S.ficheVal,
                          color: label === "Conformité"
                            ? val === "CONFORME" ? COLORS.green : val === "—" ? "#64748b" : COLORS.red
                            : "#e2e8f0",
                        }}>{val}</span>
                      </div>
                    ))}
                  </div>

                  {/* Badges défauts */}
                  <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <DefautBadge actif={selectedStack.Delamination  === true || selectedStack.Delamination  === "true"} label="Délamination" />
                    <DefautBadge actif={selectedStack.DefautSurface === true || selectedStack.DefautSurface === "true"} label="Défaut surface" />
                    <DefautBadge actif={selectedStack.Planeite      === true || selectedStack.Planeite      === "true"} label="Planéité" />
                    <DefautBadge actif={selectedStack.AutresDefauts === true || selectedStack.AutresDefauts === "true"} label="Autres défauts" />
                  </div>

                  {/* Commentaires */}
                  {[
                    ["Délamination", selectedStack.DelaminationComment],
                    ["Surface",      selectedStack.DefautSurfaceComment],
                    ["Planéité",     selectedStack.PlaneiteComment],
                    ["Autres",       selectedStack.AutresDefautsComment],
                  ].filter(([, v]) => v).map(([label, val]) => (
                    <div key={label} style={S.comment}>
                      <span style={{ color: "#64748b", fontSize: 12 }}>{label} :</span>
                      <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 6 }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Graphiques */}
                {epChart.length > 0 && (
                  <div style={S.row}>
                    <div style={S.card}>
                      <div style={S.cardTitle}>📏 Épaisseur par pli (mm)</div>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={epChart} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                          <XAxis dataKey="pli" tick={{ fill: "#64748b", fontSize: 11 }} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit=" mm" />
                          <Tooltip content={<CustomTooltip />} />
                          {selectedStack.Epaisseur_Moyenne > 0 && (
                            <ReferenceLine y={selectedStack.Epaisseur_Moyenne} stroke={COLORS.yellow}
                              strokeDasharray="6 3"
                              label={{ value: "Ép. moy.", fill: COLORS.yellow, fontSize: 11 }} />
                          )}
                          <Bar dataKey="ep" name="Épaisseur (mm)" radius={[4, 4, 0, 0]}>
                            {epChart.map((p, i) => (
                              <Cell key={i} fill={
                                selectedStack.Epaisseur_Moyenne > 0 &&
                                Math.abs(p.ep - selectedStack.Epaisseur_Moyenne) > SEUIL_EP_VAR
                                  ? COLORS.red : COLORS.teal
                              } />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={S.card}>
                      <div style={S.cardTitle}>💧 Humidité & Densité par pli</div>
                      {epChart.some(p => p.humidite > 0 || p.densite > 0) ? (
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={epChart} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                            <XAxis dataKey="pli" tick={{ fill: "#64748b", fontSize: 11 }} />
                            <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="humidite" name="Humidité (%)"    fill={COLORS.blue}   radius={[4, 4, 0, 0]} />
                            <Bar dataKey="densite"  name="Densité (g/cm³)" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={S.empty}>Pas de données BancDeTest pour ce stack</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Table des plis */}
                {plisEnrichis.length > 0 && (
                  <div style={S.card}>
                    <div style={S.cardTitle}>🪵 Détail des plis — {selectedID}</div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            {["Position", "PlyPN", "Masse (g)", "Épaisseur (mm)", "Humidité (%)", "Densité (g/cm³)", "Commentaire"].map(h => (
                              <th key={h} style={S.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {plisEnrichis.map((p, i) => (
                            <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                              <td style={{ ...S.td, color: COLORS.yellow, fontWeight: 700 }}>{p.position}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{p.plyPn || "—"}</td>
                              <td style={S.td}>{p.masse || "—"}</td>
                              <td style={{
                                ...S.td,
                                color: selectedStack.Epaisseur_Moyenne > 0 &&
                                  Math.abs(p.epaisseur - selectedStack.Epaisseur_Moyenne) > SEUIL_EP_VAR
                                    ? COLORS.red : COLORS.green,
                                fontWeight: 600,
                              }}>
                                {p.epaisseur || "—"}
                              </td>
                              <td style={S.td}>{p.humidite || "—"}</td>
                              <td style={S.td}>{p.densite  || "—"}</td>
                              <td style={{ ...S.td, color: "#64748b", fontSize: 12 }}>{p.commentaire || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tests BancDeTest */}
                {testsStack.length > 0 && (
                  <div style={S.card}>
                    <div style={S.cardTitle}>🔬 Tests BancDeTest — {selectedID}</div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            {["Date contrôle", "Lot woodoo", "Masse (g)", "Épaisseur (mm)", "Humidité moy. (%)", "Densité (g/cm³)", "Opérateur"].map(h => (
                              <th key={h} style={S.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {testsStack.map((t, i) => (
                            <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                              <td style={S.td}>{(t.Date_de_controle || "").split("T")[0]}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>{t.Lot_woodoo || "—"}</td>
                              <td style={S.td}>{toF(t.Masse) || "—"}</td>
                              <td style={S.td}>{toF(t.Epaisseur) || "—"}</td>
                              <td style={S.td}>{toF(t.Humidite_Moyenne) || "—"}</td>
                              <td style={S.td}>{toF(t.Densite) || "—"}</td>
                              <td style={{ ...S.td, color: "#64748b" }}>{t.operator || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ TAB 2 — VUE CR ══ */}
        {activeTab === "cr" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={S.card}>
              <div style={S.cardTitle}>
                📉 Taux de compression (30 derniers stacks)
                <span style={{ ...S.badge, background: "rgba(251,146,60,0.15)", color: COLORS.orange, borderColor: "rgba(251,146,60,0.3)" }}>
                  Seuil {SEUIL_CR}%
                </span>
              </div>
              {crData.length === 0 ? (
                <div style={S.empty}>Aucune donnée CR disponible</div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={crData} margin={{ top: 8, right: 20, left: -10, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                    <XAxis dataKey="stack" tick={{ fill: "#64748b", fontSize: 10 }} angle={-40} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={SEUIL_CR} stroke={COLORS.red} strokeDasharray="6 3"
                      label={{ value: `Seuil ${SEUIL_CR}%`, fill: COLORS.red, fontSize: 11, position: "right" }} />
                    <Bar dataKey="cr" name="Taux CR (%)" radius={[4, 4, 0, 0]}>
                      {crData.map((r, i) => <Cell key={i} fill={r.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {crData.filter(r => r.cr > SEUIL_CR).length > 0 && (
              <div style={S.card}>
                <div style={S.cardTitle}>
                  🚨 Stacks en alerte CR
                  <span style={{ ...S.badge, background: "rgba(248,113,113,0.15)", color: COLORS.red, borderColor: "rgba(248,113,113,0.3)" }}>
                    {crData.filter(r => r.cr > SEUIL_CR).length}
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {["ID Stack", "Taux CR (%)", "Date", "Conformité"].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {crData.filter(r => r.cr > SEUIL_CR).map((r, i) => (
                        <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                          <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}
                            onClick={() => handleSelectStack(r.stack)}>
                            {r.stack} ↗
                          </td>
                          <td style={{ ...S.td, color: COLORS.red, fontWeight: 700 }}>{r.cr}%</td>
                          <td style={S.td}>{r.date}</td>
                          <td style={S.td}>
                            <span style={{ ...S.badge,
                              background: r.conforme === "CONFORME" ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.15)",
                              color:      r.conforme === "CONFORME" ? COLORS.green : COLORS.red,
                              borderColor:r.conforme === "CONFORME" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)",
                            }}>
                              {r.conforme || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ TAB 3 — CARTE DÉFAUTS ══ */}
        {activeTab === "defauts" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={S.kpiRow}>
              {[
                ["💧", "Délamination",      stacks.filter(s => s.Delamination  === true || s.Delamination  === "true").length, COLORS.red],
                ["🪟", "Défaut surface",    stacks.filter(s => s.DefautSurface === true || s.DefautSurface === "true").length, COLORS.orange],
                ["📐", "Planéité",          stacks.filter(s => s.Planeite      === true || s.Planeite      === "true").length, COLORS.yellow],
                ["🔧", "Autres défauts",    stacks.filter(s => s.AutresDefauts === true || s.AutresDefauts === "true").length, COLORS.purple],
                ["📏", "Écart ép. > seuil", stacks.filter(s => toF(s.Ecart_Max_Ep) > SEUIL_EP_VAR).length,                   COLORS.blue],
              ].map(([icon, label, val, color]) => (
                <KpiCard key={label} icon={icon} label={label} value={val}
                  sub={`sur ${stacks.length} stacks`} color={val > 0 ? color : COLORS.gray} />
              ))}
            </div>

            <div style={S.card}>
              <div style={S.cardTitle}>🗺️ Stacks avec défauts ({defautsData.length})</div>
              {defautsData.length === 0 ? (
                <div style={S.empty}>✅ Aucun défaut actif</div>
              ) : (
                <div style={S.defautGrid}>
                  {defautsData.map((s, i) => (
                    <div key={i}
                      style={{ ...S.defautCard, cursor: "pointer",
                        borderColor: s.Conformite === "CONFORME" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.4)" }}
                      onClick={() => handleSelectStack(s.IDStack)}>
                      <div style={{ color: "#E6FF3E", fontWeight: 800, fontFamily: "monospace", fontSize: 14, marginBottom: 8 }}>
                        {s.IDStack}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 11, marginBottom: 10 }}>
                        {s.Date || s.Timestamp?.split("T")[0] || "—"} · {s.Operateur || "—"}
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(s.Delamination  === true || s.Delamination  === "true") && <DefautBadge actif label="Délam."   />}
                        {(s.DefautSurface === true || s.DefautSurface === "true") && <DefautBadge actif label="Surface"  />}
                        {(s.Planeite      === true || s.Planeite      === "true") && <DefautBadge actif label="Planéité" />}
                        {(s.AutresDefauts === true || s.AutresDefauts === "true") && <DefautBadge actif label="Autres"   />}
                        {toF(s.Ecart_Max_Ep) > SEUIL_EP_VAR && (
                          <DefautBadge actif label={`Écart ${toF(s.Ecart_Max_Ep)}mm`} />
                        )}
                      </div>
                      <div style={{ marginTop: 8, fontSize: 11, color: "#475569" }}>Voir la fiche →</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={S.card}>
              <div style={S.cardTitle}>📋 Table complète des défauts</div>
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {["ID Stack", "Date", "Délam.", "Surface", "Planéité", "Autres", "Écart max ép.", "Conformité", "Opérateur"].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {defautsData.map((s, i) => (
                      <tr key={i} style={{ ...(i % 2 === 0 ? S.trEven : S.trOdd), cursor: "pointer" }}
                        onClick={() => handleSelectStack(s.IDStack)}>
                        <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace" }}>{s.IDStack}</td>
                        <td style={S.td}>{s.Date || s.Timestamp?.split("T")[0] || "—"}</td>
                        {[s.Delamination, s.DefautSurface, s.Planeite, s.AutresDefauts].map((actif, j) => (
                          <td key={j} style={S.td}>
                            {actif === true || actif === "true"
                              ? <span style={{ color: COLORS.red }}>⚠️</span>
                              : <span style={{ color: "#1e3a50" }}>—</span>}
                          </td>
                        ))}
                        <td style={{ ...S.td, color: toF(s.Ecart_Max_Ep) > SEUIL_EP_VAR ? COLORS.red : "#64748b" }}>
                          {toF(s.Ecart_Max_Ep) || "—"}
                        </td>
                        <td style={S.td}>
                          <span style={{ ...S.badge,
                            background: s.Conformite === "CONFORME" ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.15)",
                            color:      s.Conformite === "CONFORME" ? COLORS.green : COLORS.red,
                            borderColor:s.Conformite === "CONFORME" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)",
                          }}>
                            {s.Conformite || "—"}
                          </span>
                        </td>
                        <td style={{ ...S.td, color: "#64748b" }}>{s.Operateur || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:        { minHeight: "100vh", background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)", color: "#e2e8f0", fontFamily: "'Inter',sans-serif", padding: "24px 28px" },
  wrap:        { maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  header:      { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title:       { fontSize: 26, fontWeight: 900, background: "linear-gradient(90deg,#fff,#E6FF3E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  subtitle:    { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  backBtn:     { padding: "9px 18px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#cbd5e1", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  searchInput: { padding: "8px 14px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#e2e8f0", fontSize: 13, outline: "none" },
  kpiRow:      { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 14 },
  kpiCard:     { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderTopWidth: 3, backdropFilter: "blur(8px)" },
  kpiIcon:     { fontSize: 22 },
  kpiValue:    { fontSize: 30, fontWeight: 900 },
  kpiLabel:    { fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" },
  kpiSub:      { fontSize: 11, color: "#64748b" },
  card:        { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "20px 22px", backdropFilter: "blur(8px)" },
  cardTitle:   { fontSize: 13, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  badge:       { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontWeight: 700, fontSize: 11, border: "1px solid" },
  row:         { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  tabBar:      { display: "flex", gap: 6, borderBottom: "1px solid #1e3a50" },
  tab:         { padding: "10px 20px", borderRadius: "8px 8px 0 0", border: "1px solid transparent", background: "transparent", color: "#64748b", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  tabActive:   { background: "rgba(255,255,255,0.05)", border: "1px solid #1e3a50", borderBottomColor: "#041e2e", color: "#E6FF3E" },
  ficheGrid:   { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 12 },
  ficheItem:   { display: "flex", flexDirection: "column", gap: 3, padding: "10px 14px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid #1e3a50" },
  ficheLabel:  { fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em" },
  ficheVal:    { fontSize: 15, fontWeight: 700 },
  comment:     { marginTop: 8, padding: "8px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 6, border: "1px solid #1e3a50" },
  defautGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px,1fr))", gap: 12 },
  defautCard:  { padding: "14px 16px", background: "rgba(255,255,255,0.02)", borderRadius: 10, border: "1px solid" },
  tooltip:     { background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8, padding: "10px 14px" },
  empty:       { textAlign: "center", padding: "40px 20px", color: "#64748b", fontSize: 13 },
  table:       { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:          { padding: "11px 14px", background: "#071e2f", color: "#94a3b8", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "1px solid #1e3a50", whiteSpace: "nowrap" },
  td:          { padding: "11px 14px", borderBottom: "1px solid #0f2a3d", color: "#cbd5e1", whiteSpace: "nowrap" },
  trEven:      { background: "transparent" },
  trOdd:       { background: "rgba(255,255,255,0.015)" },
  stackList:   { display: "flex", flexWrap: "wrap", gap: 6, flex: 1 },
  stackBtn:    { padding: "5px 12px", borderRadius: 6, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.03)", color: "#94a3b8", cursor: "pointer", fontSize: 12 },
  stackBtnActive: { background: "rgba(230,255,62,0.1)", borderColor: "#E6FF3E", color: "#E6FF3E" },
};